// src/components/hooks/useProfiles.ts
import { useState, useEffect, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

// ============================================================
// TIPOS
// ============================================================

export interface Perfil {
  nombre_perfil: string;
  id_perfil: string;
  id_empresa: string | null;
  empresa: string | null;
  perfil: 'empresa' | 'trabajador' | 'cliente' | 'administrador';
  ruta_panel: string | null;
  logo?: string | null;
  descripcion?: string | null;
  inicio_automatico?: boolean;
  id_usuario?: string;
  // Solo relevante para perfiles 'trabajador': TRUE si su empresa no tiene
  // plan efectivo o él no tiene acceso. Lo calcula vista_usuarios_detalle.
  acceso_bloqueado?: boolean;
}

export interface PerfilDadoBaja {
  id_usuario: string;
  id_perfil: string;
  id_empresa: string | null;
  perfil: string;
  nombre_perfil: string;
  ruta_panel: string | null;
  fecha_baja: string | null;
}

// ============================================================
// HOOK PRINCIPAL
// ============================================================

export function useProfiles() {
  const [perfiles, setPerfiles] = useState<Perfil[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // ============================================================
  // FETCH PERFILES (estado/UI)
  // ============================================================
  const fetchPerfiles = useCallback(async () => {
    setLoading(true);
    setError('');

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      if (userError) Sentry.captureException(userError);
      setError('Usuario no autenticado.');
      setPerfiles([]);
      setLoading(false);
      return;
    }

    const { data, error: perfilesError } = await supabase
      .from('vista_usuarios_detalle')
      .select('*')
      .eq('id_usuario', user.id)
      .order('empresa', { ascending: true })
      .order('perfil', { ascending: true });

    if (perfilesError || !data) {
      if (perfilesError)
        Sentry.captureException(perfilesError, {
          extra: { idUsuario: user.id },
        });
      setError('No se pudieron obtener los perfiles.');
      setPerfiles([]);
    } else {
      setPerfiles(data as Perfil[]);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPerfiles();
  }, [fetchPerfiles]);

  // ============================================================
  // FUNCIÓN SEGURA PARA LOGIN / AUTORIZACIÓN
  // ============================================================
  const hasPerfil = useCallback(async (perfilBuscado: Perfil['perfil']) => {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) return false;

    const { data, error } = await supabase
      .from('vista_usuarios_detalle')
      .select('perfil')
      .eq('id_usuario', user.id);

    if (error || !data) {
      if (error)
        Sentry.captureException(error, {
          extra: { idUsuario: user.id, perfilBuscado },
        });
      return false;
    }

    return data.some(
      (p: { perfil: Perfil['perfil'] }) => p.perfil === perfilBuscado
    );
  }, []);

  // ============================================================
  // GET PERFILES PARA USO EN OAuthCallback
  // ============================================================
  const getPerfilesUsuario = useCallback(async (idUsuario: string) => {
    if (!idUsuario) return [];

    const { data, error } = await supabase
      .from('vista_usuarios_detalle')
      .select('*')
      .eq('id_usuario', idUsuario);

    if (error) {
      Sentry.captureException(error, { extra: { idUsuario } });
      return [];
    }

    return data || [];
  }, []);

  // ============================================================
  // PERFIL DE CLIENTE DADO DE BAJA
  //
  // vista_usuarios_detalle filtra activo = true, así que un perfil dado de
  // baja no aparece en ninguna de las funciones de arriba. Sin esto, quien se
  // da de baja y vuelve a entrar parece un usuario sin perfiles y acaba en el
  // registro o en la landing.
  //
  // Solo el perfil de cliente puede darse de baja y reactivarse a sí mismo:
  // al trabajador lo gestiona su empresa y la baja de un negocio se tramita
  // por correo. De ahí el filtro por fecha_baja, que marca la baja voluntaria.
  // ============================================================
  const getPerfilClienteDesactivado = useCallback(
    async (idUsuario: string): Promise<PerfilDadoBaja | null> => {
      if (!idUsuario) return null;

      const { data, error } = await supabase
        .from('vista_usuarios_perfiles_todos')
        .select(
          'id_usuario, id_perfil, id_empresa, perfil, nombre_perfil, ruta_panel, fecha_baja'
        )
        .eq('id_usuario', idUsuario)
        .eq('perfil', 'cliente')
        .eq('activo', false)
        .not('fecha_baja', 'is', null)
        .limit(1)
        .maybeSingle();

      if (error) {
        Sentry.captureException(error, { extra: { idUsuario } });
        return null;
      }

      return (data as PerfilDadoBaja) ?? null;
    },
    []
  );

  // ============================================================
  // UPDATE: INICIO AUTOMÁTICO
  // ============================================================
  const updateInicioAutomatico = useCallback(
    async (perfil: Perfil) => {
      if (!perfil?.id_usuario || !perfil?.id_perfil) return;

      setLoading(true);

      try {
        const off = await supabase
          .from('usuarios_perfiles')
          .update({ inicio_automatico: false })
          .eq('id_usuario', perfil.id_usuario);

        if (off.error) throw off.error;

        if (!perfil.inicio_automatico) {
          let query = supabase
            .from('usuarios_perfiles')
            .update({ inicio_automatico: true })
            .eq('id_usuario', perfil.id_usuario)
            .eq('id_perfil', perfil.id_perfil);

          if (perfil.id_empresa) {
            query = query.eq('id_empresa', perfil.id_empresa);
          }

          const on = await query;
          if (on.error) throw on.error;
        }

        await fetchPerfiles();
        toast.success('Inicio automático actualizado.');
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            idUsuario: perfil.id_usuario,
            idPerfil: perfil.id_perfil,
            idEmpresa: perfil.id_empresa,
          },
        });
        toast.error('No se pudo actualizar el inicio automático.');
      } finally {
        setLoading(false);
      }
    },
    [fetchPerfiles]
  );

  // ============================================================
  // PERFILES ORDENADOS (admin primero)
  // ============================================================
  const perfilesOrdenados = useMemo(() => {
    return [...perfiles].sort((a, b) => {
      if (a.perfil === 'administrador') return -1;
      if (b.perfil === 'administrador') return 1;
      return 0;
    });
  }, [perfiles]);

  // ============================================================
  // RETURN
  // ============================================================
  return {
    perfiles,
    perfilesOrdenados,
    loading,
    error,
    refetch: fetchPerfiles,
    updateInicioAutomatico,
    hasPerfil,
    getPerfilesUsuario,
    getPerfilClienteDesactivado,
  };
}